import type { FilterDefinition } from "../config/structured-search-schema";
import {
  ANY_VERSION_VALUE,
  formatVersionedEntitySuggestionLabel,
} from "../config/versioned-entity-utils";
import type { BuilderStep } from "../stores";

import FieldSuggestionsSection from "./FieldSuggestionsSection";
import OperatorOptionRow from "./OperatorOptionRow";
import UnavailableSuggestionsSection from "./UnavailableSuggestionsSection";
import ValueOptionRow from "./ValueOptionRow";
import type {
  FieldSuggestionItem,
  OperatorSuggestionItem,
  PlainTextSearchSuggestionItem,
  SuggestionItem,
  ValueSuggestionItem,
} from "./types";
import { SuggestionStepEnum } from "./types";

const normalizePromptLabel = (value: string): string => value.trim().toLowerCase();

const selectedPromptMatchesSuggestion = (
  selectedValue: string,
  suggestionValue: ValueSuggestionItem,
): boolean => {
  if (selectedValue.startsWith(`${suggestionValue.value}:`)) {
    return true;
  }

  const promptName = suggestionValue.prompt_name;
  if (!promptName) {
    return false;
  }

  const normalizedSelectedValue = normalizePromptLabel(selectedValue);
  const normalizedPromptName = normalizePromptLabel(promptName);
  return (
    normalizedSelectedValue === normalizedPromptName ||
    normalizedSelectedValue.startsWith(`${normalizedPromptName} v`)
  );
};

interface AutocompleteSuggestionsRouterProps {
  step: BuilderStep;
  suggestions: SuggestionItem[];
  disabledFieldSuggestions: FieldSuggestionItem[];
  lockedFieldSuggestions: FieldSuggestionItem[];
  disabledOperatorSuggestions: OperatorSuggestionItem[];
  disabledPlainTextSearchSuggestion: PlainTextSearchSuggestionItem | null;
  unavailableSuggestionsReason: string;
  highlightedIndex: number;
  onSelect: (index: number) => void;
  filterDefinition?: FilterDefinition;
  isPromptField: boolean;
  isTraceVersionedEntityField: boolean;
  isMultiSelectMode: boolean;
  multiValues: string[];
  promptVersionStep: boolean;
  currentEntityId?: number;
}

const AutocompleteSuggestionsRouter = ({
  step,
  suggestions,
  disabledFieldSuggestions,
  lockedFieldSuggestions,
  disabledOperatorSuggestions,
  disabledPlainTextSearchSuggestion,
  unavailableSuggestionsReason,
  highlightedIndex,
  onSelect,
  filterDefinition,
  isPromptField,
  isTraceVersionedEntityField,
  isMultiSelectMode,
  multiValues,
  promptVersionStep,
  currentEntityId,
}: AutocompleteSuggestionsRouterProps) => {
  const isPlainTextSearchSuggestion = (
    item: SuggestionItem,
  ): item is PlainTextSearchSuggestionItem => "type" in item && item.type === "plain_text_search";

  switch (step) {
    case SuggestionStepEnum.FIELD: {
      const plainTextSearchSuggestion = suggestions.find(isPlainTextSearchSuggestion);
      const fieldSuggestions = suggestions.filter(
        (item): item is FilterDefinition => !isPlainTextSearchSuggestion(item),
      );

      return (
        <>
          {plainTextSearchSuggestion && (
            <ValueOptionRow
              value={plainTextSearchSuggestion.value}
              renderedValue={plainTextSearchSuggestion.label}
              highlighted={highlightedIndex === 0}
              isMulti={false}
              isSelected={false}
              onSelect={() => onSelect(0)}
            />
          )}
          {plainTextSearchSuggestion && fieldSuggestions.length > 0 && (
            <li role="presentation" className="mx-2 px-2.5 py-1">
              <div aria-hidden className="h-px w-full bg-slate-100" />
            </li>
          )}
          <FieldSuggestionsSection
            suggestions={fieldSuggestions}
            highlightedIndex={plainTextSearchSuggestion ? highlightedIndex - 1 : highlightedIndex}
            onSelect={index => onSelect(plainTextSearchSuggestion ? index + 1 : index)}
          />
          {(disabledPlainTextSearchSuggestion || disabledFieldSuggestions.length > 0) && (
            <UnavailableSuggestionsSection reason={unavailableSuggestionsReason}>
              {disabledPlainTextSearchSuggestion && (
                <ValueOptionRow
                  value={disabledPlainTextSearchSuggestion.value}
                  renderedValue={disabledPlainTextSearchSuggestion.label}
                  highlighted={false}
                  disabled
                  isMulti={false}
                  isSelected={false}
                />
              )}
              <FieldSuggestionsSection
                suggestions={disabledFieldSuggestions}
                highlightedIndex={-1}
                disabled
              />
            </UnavailableSuggestionsSection>
          )}
          {lockedFieldSuggestions.length > 0 && (
            <UnavailableSuggestionsSection reason="Prompt is fixed for this version view.">
              <FieldSuggestionsSection
                suggestions={lockedFieldSuggestions}
                highlightedIndex={-1}
                disabled
              />
            </UnavailableSuggestionsSection>
          )}
        </>
      );
    }

    case SuggestionStepEnum.KEY:
      return suggestions.map((item, index) => {
        const value = item as ValueSuggestionItem;
        return (
          <ValueOptionRow
            key={value.value}
            value={value.value}
            renderedValue={value.label ?? value.value}
            count={value.count}
            highlighted={index === highlightedIndex}
            isMulti={false}
            isSelected={false}
            onSelect={() => onSelect(index)}
          />
        );
      });

    case SuggestionStepEnum.OPERATOR:
      return (
        <>
          {suggestions.map((item, index) => {
            const operator = item as OperatorSuggestionItem;

            return (
              <OperatorOptionRow
                key={operator.operator}
                operator={operator.operator}
                display={operator.display}
                highlighted={index === highlightedIndex}
                onSelect={() => onSelect(index)}
              />
            );
          })}
          {disabledOperatorSuggestions.length > 0 && (
            <UnavailableSuggestionsSection reason={unavailableSuggestionsReason}>
              {disabledOperatorSuggestions.map(operator => (
                <OperatorOptionRow
                  key={operator.operator}
                  operator={operator.operator}
                  display={operator.display}
                  highlighted={false}
                  disabled
                />
              ))}
            </UnavailableSuggestionsSection>
          )}
        </>
      );

    case SuggestionStepEnum.VALUE: {
      return suggestions.map((item, index) => {
        const valueSuggestion = item as ValueSuggestionItem;
        let isSelected = isMultiSelectMode && multiValues.includes(valueSuggestion.value);
        if (isMultiSelectMode && (isPromptField || isTraceVersionedEntityField)) {
          if (promptVersionStep && currentEntityId !== undefined) {
            const entitySelectionKey =
              valueSuggestion.value === ANY_VERSION_VALUE
                ? `${currentEntityId}:any`
                : `${currentEntityId}:${valueSuggestion.value}`;
            isSelected = multiValues.includes(entitySelectionKey);
          } else {
            isSelected = multiValues.some(selectedValue =>
              selectedPromptMatchesSuggestion(selectedValue, valueSuggestion),
            );
          }
        }

        return (
          <ValueOptionRow
            key={valueSuggestion.value}
            value={valueSuggestion.value}
            renderedValue={formatVersionedEntitySuggestionLabel({
              suggestion: valueSuggestion,
              versionStep: promptVersionStep,
            })}
            count={valueSuggestion.count}
            definition={filterDefinition}
            highlighted={index === highlightedIndex}
            isMulti={isMultiSelectMode}
            isSelected={isSelected}
            onSelect={() => onSelect(index)}
          />
        );
      });
    }

    default:
      return null;
  }
};

export default AutocompleteSuggestionsRouter;
